// Imports ------------------------------------------------------------------->

import {inject} from "../di";
import {ConfigService} from "../ConfigService";
import {IAuthenticationService, AuthUser, AuthProviderUser, AuthStatus} from "../definitions/IAuthenticationService";
import {AuthProvider, PostMessageEvent} from "../../definitions/CommonDefinitions";
import {fetchJSON} from "../../utils/fetch";
import {generateUUID} from "../../utils/uuid";

// Private Definitions ------------------------------------------------------->

const OAUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const VALIDATION_URL = "https://www.googleapis.com/oauth2/v3/tokeninfo";
const PROFILE_URL = "https://www.googleapis.com/plus/v1/people/me";

// Public Definitions -------------------------------------------------------->

export class GoogleAuthenticator implements IAuthenticationService {

	@inject config: ConfigService;

	/* FIXME: There is some overlap in functionality between this class and FacebookAuthenticator.ts (eg: loginPromise,
		*        active login window, etc). Consider abstracting this into a common base class */
	private userInfo: AuthProviderUser;
	private loginPromise: Promise<void>;
	private activePopup: Window;

	get status() {
		return this.userInfo ? AuthStatus.LOGGED_IN : AuthStatus.LOGGED_OUT;
	}

	get user() {
		return {
			info: this.userInfo
		} as AuthUser;
	}

	init = () => {
		return new Promise<void>((resolve, reject) => {
			const loginCookie = Cookies.get("flipp-user");
			if (loginCookie) {
				const data = JSON.parse(loginCookie);
				this.userInfo = data;

				/* Don't automatically log the user out when their Google token expires.
					* See: FW-712: User should stay logged in until they log out
					* Keeping the code below as reference as we may re-introduce it as part of redemption work
					*/
				// const now = new Date().getTime();
				// if (now > data.expiresAt) {
				//   Cookies.remove("flipp-login");
				//   Cookies.remove("flipp-user");
				// } else {
				//   this.userInfo = data;
				// }
			}
			resolve();
		});
	}

	login = (source: AuthProvider, target?: EventTarget) => {
		/* If login already exists, return existing login Promise and focus the open window */
		if (this.loginPromise) {
			if (this.activePopup) {
				this.activePopup.focus();
			}
			return this.loginPromise;
		}

		let windowOpenCheck: number;
		this.loginPromise = new Promise((resolve, reject) => {
			/* Construct oauth URL */
			const uuid = window.btoa(generateUUID());
			const redirectUrl = `${window.location.protocol}//${window.location.host}/login_redirect`;
			const state = JSON.stringify({
				provider: AuthProvider.GOOGLE,
				path: window.location.pathname
			});
			const authUrl = `${OAUTH_URL}?response_type=token%20id_token&prompt=select_account&scope=email%20profile&redirect_uri=${redirectUrl}&client_id=${this.config.GOOGLE_CLIENT_ID}&state=${encodeURIComponent(state)}&nonce=${uuid}&disallow_webview=true`;

			/* Setup listener for "postMessage" events */
			const receiveMessage = (evt: PostMessageEvent) => {
				const message = evt.data;
				if (message.type !== "auth-login") {
					return;
				}
				if (message.result !== "success" || evt.origin !== window.location.origin) {
					this._resetLogin();
					reject(new Error());
					return;
				}

				/* Message is valid at this point - do cleanup */
				window.removeEventListener("message", receiveMessage);
				clearInterval(windowOpenCheck);
				windowOpenCheck = -1;
				this._resetLogin();

				/* Fetch the user profile */
				this._fetchUserProfile(message.data.access_token).then((profile) => {
					this.userInfo = {
						id: profile.id,
						name: profile.displayName,
						image: profile.image ? profile.image.url : "",
						email: profile.emails && Array.isArray(profile.emails) && profile.emails.length > 0 ?
											profile.emails[0].value : "",
						accessToken: message.data.id_token,
						expiresAt: new Date().getTime() + Number(message.data.expires_in) * 1000,
						provider: AuthProvider.GOOGLE
					};

					/* Store the auth values in a Cookie instead of localStorage because some Browser modes (eg: Safari Private)
						* prevent access to localStorage */
					Cookies.set("flipp-user", JSON.stringify(this.userInfo), {
						expires: 30,
						domain: ".flipp.com"
					});
					resolve();
				}).catch((e: Error) => {
					this._resetLogin();
					reject(e);
				});
			};
			window.addEventListener("message", receiveMessage);

			/* Open oauth URL in popup */
			const width = 600;
			const height = 600;
			const left = (screen.width / 2) - (width / 2);
			const top = (screen.height / 2) - (height / 2);
			this.activePopup = window.open(authUrl, "Flipp", `height=${height},width=${width},top=${top},left=${left}`);
			if (this.activePopup.focus) {
				this.activePopup.focus();
			}

			/* Poll to see if Login window is closed, and call reject() if it is */
			windowOpenCheck = setInterval(() => {
				if (!this.activePopup || this.activePopup.closed) {
					this._resetLogin();
					window.removeEventListener("message", receiveMessage);
					clearInterval(windowOpenCheck);
					windowOpenCheck = -1;
					reject(new Error());
				}
			}, 3000);
		});

		return this.loginPromise;
	}

	logout = () => {
		return Promise.resolve().then(() => {
			this.userInfo = null;
			Cookies.remove("flipp-user", { domain: ".flipp.com" });
			Cookies.remove("flipp-user");
		});
	}

	private _resetLogin = () => {
		this.activePopup = null;
		this.loginPromise = null;
	}

	private _fetchUserProfile = (token: string) => {
		const profileUrl = `${PROFILE_URL}?access_token=${token}`;
		const validation = `${VALIDATION_URL}?access_token=${token}`;
		/* Call two different endpoints here:
			*  1) Profile endpoint on Google to get name/email/image
			*  2) Validation endpoint on Google to verify the provided access token is correct
			*      See: https://developers.google.com/identity/protocols/OAuth2UserAgent#validatetoken
			*    If validation fails, we throw an error (even if token is validated)
			*/
		return Promise.all<any, any>([
			fetchJSON(profileUrl),
			fetchJSON(validation)
		]).then(([profile, validation]) => {
			if ("error" in validation) {
				throw new Error();
			}
			return profile;
		});
	}
}
